import {
  EFormFieldOptionsSourceKind,
  EFormFieldSource,
  ICRUDFormDiMetadata,
  ICRUDFormField,
  IActionDefinitionDto,
  ICRUDFieldOptionsSource,
  IFormSection,
  IWFParameter
} from "@kuika/kuika-cl-model";
import { Guid } from "guid-typescript";
import { useEffect, useRef, useState } from "react";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaCheckbox from "../../../ui-kit/kuika-checkbox/kuika-checkbox";
import KuikaDropdownMenu from "../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTextMedium from "../../../ui-kit/typography/TextMedium/TextMedium";
import {
  CUSTOM_FIELD_TYPES,
  defaultElementTypeFor,
  getElementTypeOptions,
  IElementTypeOption,
  IFieldTypeOption
} from "./form-element-type-options";
import { moveFormField, moveFormFieldToNewSection, removeFormField } from "./forms-tab-fields";
import FormsTabLayoutDrawer from "./forms-tab-layout-drawer";
import {
  isOptionsCapableElementType,
  optionActionLabel,
  shouldPromptForOptionsSource
} from "./forms-tab-options-source";
import FormsTabOptionsSourceModal from "./forms-tab-options-source-modal";
import FormsTabStepOutputs from "./forms-tab-step-outputs";

export interface IFormsTabFormProps {
  metadata: ICRUDFormDiMetadata;
  /** Header title for the form card (bound screen name, or "<step> Form"). */
  formTitle: string;
  /** The step's outputs resolved to parameter metadata — the informational Step Outputs chip strip. */
  outputParams: IWFParameter[];
  /** Parameter ids this step maps as outputs — auto-selected, badged "Output", warned when unselected. */
  outputParamIds: Set<string>;
  /** Ids of Business Object parameters, to tell BO fields from internal ones in the badge. */
  boParamIds: Set<string>;
  /** Fully-qualified lookup table type names, forwarded to the element-type resolver. */
  tableTypeNames: string[];
  /**
   * Actions a SelectBox / MultiSelect field may draw its options from — already filtered by the
   * shell (project-wide, list-returning, needing no parameter binding). Empty is a valid state
   * and the modal says so rather than showing an empty picker.
   */
  optionSourceActions: IActionDefinitionDto[];
  /** True when this step's screen is an unsaved draft — shows the amber "apply to save" banner. */
  isDraft?: boolean;
  onChange: (metadata: ICRUDFormDiMetadata) => void;
  /** Open the parameter picker (internal params) targeting the given section. */
  onOpenAddField: (sectionId: string) => void;
}

interface IFieldBadge {
  text: string;
  kind: "bo" | "internal" | "custom";
}

/** The field an options-source modal is editing, resolved from the live metadata. */
interface IOptionsSourceTarget {
  section: IFormSection;
  field: ICRUDFormField;
}

/** Where a dragged field would land: an insertion slot in a section's current item list. */
interface IFieldDropTarget {
  sectionId: string;
  index: number;
}

/** Pseudo section id for the trailing ghost drop target (drag a field here → new section). */
const GHOST_SECTION_ID = "__ghost-section__";

/** A grip mousedown that hasn't (yet) travelled far enough to count as a drag. */
interface IDragSession {
  fieldId: string;
  startY: number;
  /** Becomes true once the pointer moved past the threshold — only then do visuals engage. */
  active: boolean;
}

/** Pointer travel (px) before a grip press becomes a drag — keeps plain clicks inert. */
const DRAG_THRESHOLD_PX = 3;

/**
 * The form editor for one user task: an optional draft banner, the form card header (with the
 * informational Step Outputs strip + reference note), and one table per section. Each row exposes
 * the field's label, its bound parameter, data type, element type, and the Required / Read-Only
 * flags. Output-mapped fields are pre-selected and badged "Output"; unselecting one warns the user
 * (the step still writes it back).
 */
const FormsTabForm = ({
  metadata,
  formTitle,
  outputParams,
  outputParamIds,
  boParamIds,
  tableTypeNames,
  optionSourceActions,
  isDraft,
  onChange,
  onOpenAddField
}: IFormsTabFormProps) => {
  // The field being dragged, for the row dim.
  const [dragFieldId, setDragFieldId] = useState<string | null>(null);
  // The insertion slot under the pointer — drives the between-rows line AND the section highlight.
  const [dropTarget, setDropTarget] = useState<IFieldDropTarget | null>(null);
  // Whether the Form Layout drawer is open.
  const [layoutOpen, setLayoutOpen] = useState<boolean>(false);
  // The field whose options-source modal is open, as `${sectionId}:${fieldId}` — the pair is the
  // only stable address, since a field id is unique per form but the section owns the mutation.
  const [optionsSourceTarget, setOptionsSourceTarget] = useState<string | null>(null);
  // Geometry root for drag hit-testing (sections + rows are queried inside it).
  const formRef = useRef<HTMLDivElement | null>(null);
  // The in-flight grip drag, if any. A ref, not state: mousemove must read it synchronously.
  const dragSessionRef = useRef<IDragSession | null>(null);
  // Latest metadata for the document-level mouseup closure (props can move under a long drag).
  const metadataRef = useRef<ICRUDFormDiMetadata>(metadata);
  metadataRef.current = metadata;
  // Detaches the document listeners of the in-flight drag; invoked on mouseup and on unmount.
  const detachDragListenersRef = useRef<(() => void) | null>(null);
  useEffect(
    () => (): void => {
      detachDragListenersRef.current?.();
    },
    []
  );

  const isOutput = (field: ICRUDFormField): boolean =>
    !!field.linkedWorkflowBoParameterId && outputParamIds.has(field.linkedWorkflowBoParameterId);

  /** A field bound to a Business Object parameter — always present, so it can be unselected but never deleted. */
  const isBoParam = (field: ICRUDFormField): boolean =>
    !!field.linkedWorkflowBoParameterId && boParamIds.has(field.linkedWorkflowBoParameterId);

  const badgeFor = (field: ICRUDFormField): IFieldBadge => {
    if (field.source !== EFormFieldSource.WorkflowBusinessObjectParameter) {
      return { text: "Custom", kind: "custom" };
    }
    if (field.linkedWorkflowBoParameterId && boParamIds.has(field.linkedWorkflowBoParameterId)) {
      return { text: "Business Object", kind: "bo" };
    }
    return { text: "Internal", kind: "internal" };
  };

  const mutateField = (sectionId: string, fieldId: string, patch: Partial<ICRUDFormField>): void => {
    const next: ICRUDFormDiMetadata = structuredClone(metadata);
    const section: IFormSection | undefined = next.sections.find((s: IFormSection): boolean => s.id === sectionId);
    if (!section) return;
    const index: number = section.items.findIndex((f: ICRUDFormField): boolean => f.id === fieldId);
    if (index < 0) return;
    section.items[index] = { ...section.items[index], ...patch };
    onChange(next);
  };

  const removeField = (sectionId: string, fieldId: string): void => {
    // A non-first section emptied by the removal disappears with it (removeFormField prunes).
    const next: ICRUDFormDiMetadata = removeFormField(metadata, sectionId, fieldId);
    if (next !== metadata) onChange(next);
  };

  const addSection = (): void => {
    const template: IFormSection | undefined = metadata.sections[metadata.sections.length - 1];
    if (!template) return;
    const next: ICRUDFormDiMetadata = structuredClone(metadata);
    next.sections.push({
      id: Guid.create().toString(),
      title: null,
      items: [],
      responsiveLayoutSizes: structuredClone(template.responsiveLayoutSizes)
    });
    onChange(next);
  };

  /** Rename a section. An empty title falls back to the "Section N" placeholder at render time. */
  const setSectionTitle = (sectionId: string, title: string): void => {
    const next: ICRUDFormDiMetadata = structuredClone(metadata);
    const section: IFormSection | undefined = next.sections.find((s: IFormSection): boolean => s.id === sectionId);
    if (!section) return;
    section.title = title.length > 0 ? title : null;
    onChange(next);
  };

  /** Append a blank, unbound custom field to a section — the user fills in label / type / element inline. */
  const addCustomField = (sectionId: string): void => {
    const next: ICRUDFormDiMetadata = structuredClone(metadata);
    const section: IFormSection | undefined = next.sections.find((s: IFormSection): boolean => s.id === sectionId);
    if (!section) return;
    section.items.push({
      id: Guid.create().toString(),
      isEnabled: true,
      name: "",
      typeName: "String",
      elementType: defaultElementTypeFor("String"),
      isRequired: false,
      isReadonlyField: false,
      decimalScale: null,
      source: EFormFieldSource.Custom
    });
    onChange(next);
  };

  const clearDragState = (): void => {
    setDragFieldId(null);
    setDropTarget(null);
  };

  /**
   * Where a pointer position lands: the section under it (real or ghost) and the insertion slot —
   * the number of field rows in that section whose vertical midpoint sits above the pointer.
   * Pure geometry over the rendered DOM (sections carry `data-dnd-section-id`), shared by
   * mousemove (indicator) and mouseup (the actual move).
   */
  const hitTestSlot = (clientX: number, clientY: number): IFieldDropTarget | null => {
    const root: HTMLDivElement | null = formRef.current;
    if (!root) return null;
    const sections: HTMLDivElement[] = Array.from(root.querySelectorAll<HTMLDivElement>("[data-dnd-section-id]"));
    for (const el of sections) {
      const rect: DOMRect = el.getBoundingClientRect();
      const inside: boolean =
        clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
      if (!inside) continue;
      const sectionId: string = el.dataset.dndSectionId ?? "";
      if (sectionId === GHOST_SECTION_ID) return { sectionId, index: 0 };
      const rows: HTMLDivElement[] = Array.from(
        el.querySelectorAll<HTMLDivElement>(".kuika-wf-forms__row:not(.kuika-wf-forms__row--head)")
      );
      let index: number = 0;
      for (const row of rows) {
        const rowRect: DOMRect = row.getBoundingClientRect();
        if (clientY > rowRect.top + rowRect.height / 2) index += 1;
      }
      return { sectionId, index };
    }
    return null;
  };

  /**
   * Pointer-based field dragging (mousedown on the grip → document-level mousemove/mouseup).
   * Deliberately NOT HTML5 drag-and-drop: native DnD proved unreliable here across three
   * iterations (same-section dragover delivery / dragstart re-render cancellation quirks), while
   * plain mouse events are deterministic in every browser. Visual feedback is the same trio —
   * dimmed source row, section highlight, insertion line — and the drop applies the same pure
   * helpers ({@link moveFormField} / {@link moveFormFieldToNewSection}), which no-op safely.
   */
  const handleGripMouseDown = (e: React.MouseEvent<HTMLSpanElement>, fieldId: string): void => {
    if (e.button !== 0) return;
    e.preventDefault(); // No text selection while dragging.
    dragSessionRef.current = { fieldId, startY: e.clientY, active: false };

    const onMouseMove = (ev: MouseEvent): void => {
      const session: IDragSession | null = dragSessionRef.current;
      if (!session) return;
      if (!session.active) {
        if (Math.abs(ev.clientY - session.startY) < DRAG_THRESHOLD_PX) return;
        session.active = true;
        setDragFieldId(session.fieldId);
        document.body.style.cursor = "grabbing";
      }
      const slot: IFieldDropTarget | null = hitTestSlot(ev.clientX, ev.clientY);
      setDropTarget(slot);
    };

    const onMouseUp = (ev: MouseEvent): void => {
      detachDragListenersRef.current?.();
      const session: IDragSession | null = dragSessionRef.current;
      dragSessionRef.current = null;
      clearDragState();
      if (!session?.active) return;
      // Re-hit-test at release: state may lag a fast pointer by a frame.
      const slot: IFieldDropTarget | null = hitTestSlot(ev.clientX, ev.clientY);
      if (!slot) return;
      const current: ICRUDFormDiMetadata = metadataRef.current;
      const next: ICRUDFormDiMetadata =
        slot.sectionId === GHOST_SECTION_ID
          ? moveFormFieldToNewSection(current, session.fieldId)
          : moveFormField(current, session.fieldId, slot.sectionId, slot.index);
      if (next !== current) onChange(next);
    };

    detachDragListenersRef.current = (): void => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "";
      detachDragListenersRef.current = null;
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const hasCustomField: boolean = metadata.sections.some((s: IFormSection): boolean =>
    s.items.some((f: ICRUDFormField): boolean => f.source === EFormFieldSource.Custom)
  );

  /**
   * The field the options-source modal is open for, resolved from the live metadata rather than
   * captured when the modal opened — so a concurrent reconcile (a parameter added on the
   * Parameters tab appends fields) can never leave the modal editing a stale copy. An address
   * that no longer resolves closes the modal by rendering nothing.
   */
  const optionsSourceField: IOptionsSourceTarget | undefined = ((): IOptionsSourceTarget | undefined => {
    if (optionsSourceTarget === null) return undefined;
    const separator: number = optionsSourceTarget.indexOf(":");
    if (separator < 0) return undefined;
    const sectionId: string = optionsSourceTarget.slice(0, separator);
    const fieldId: string = optionsSourceTarget.slice(separator + 1);
    const section: IFormSection | undefined = metadata.sections.find(
      (candidate: IFormSection): boolean => candidate.id === sectionId
    );
    const field: ICRUDFormField | undefined = section?.items.find(
      (candidate: ICRUDFormField): boolean => candidate.id === fieldId
    );
    if (section === undefined || field === undefined) return undefined;
    return { section, field };
  })();

  /**
   * How a field's declared options source reads in one line. An unbound dropdown is called out
   * rather than left blank: blank looks configured, and the component would ship EMPTY.
   */
  const describeOptionsSource = (source: ICRUDFieldOptionsSource | null | undefined): string => {
    // `== null` on purpose, and everywhere in this file: the server serializes an unset source
    // as JSON `null`, so an `=== undefined` check lets it through and the next property read
    // throws — which is exactly how this cell white-screened the whole tab twice.
    if (source == null || source.kind === EFormFieldOptionsSourceKind.None) {
      return "Not set";
    }
    if (source.kind === EFormFieldOptionsSourceKind.FixedList) {
      const count: number = source.fixedItems?.length ?? 0;
      return count === 1 ? "Fixed list · 1 option" : `Fixed list · ${count} options`;
    }
    const bound: IActionDefinitionDto | undefined = optionSourceActions.find(
      (action: IActionDefinitionDto): boolean => action.id === source.actionDefinitionId
    );
    // An action that is no longer offerable (deleted, deprecated, gained a required parameter)
    // still has to READ as something, and its stored name is the honest answer.
    return bound === undefined ? (source.actionName ?? "Action") : optionActionLabel(bound);
  };

  /**
   * Change a field's element type, opening the options editor the moment the field BECOMES a
   * dropdown with nothing in it.
   *
   * The generated SelectBox ships empty unless a source is declared, and the trigger beside the
   * select is easy not to notice — so the one moment the question is unmissable is the moment the
   * answer starts mattering. Only on the transition INTO an options-capable type, and only while
   * the field has no source yet: switching SelectBox → MultiSelect, or returning to a field that is
   * already configured, must not reopen a modal the user did not ask for.
   */
  const changeElementType = (section: IFormSection, field: ICRUDFormField, elementType: string): void => {
    const prompt: boolean = shouldPromptForOptionsSource(field.elementType, elementType, field.optionsSource);
    mutateField(section.id, field.id, { elementType });
    if (prompt) setOptionsSourceTarget(`${section.id}:${field.id}`);
  };

  /**
   * The options-source trigger, which lives INSIDE the Element Type cell.
   *
   * It had its own column, and that column cost a table width for a control only two of the
   * element types can use. It now sits next to the select that decides whether it applies at all:
   * present for SelectBox / MultiSelect, absent otherwise, so the row never has to say "not
   * applicable". The summary it used to print moved to the tooltip — the same sentence, without a
   * column of its own.
   */
  const renderOptionsSourceTrigger = (section: IFormSection, field: ICRUDFormField) => {
    if (!isOptionsCapableElementType(field.elementType)) return null;

    const source: ICRUDFieldOptionsSource | null | undefined = field.optionsSource;
    const isUnset: boolean = source == null || source.kind === EFormFieldOptionsSourceKind.None;

    return (
      <button
        type="button"
        className={`kuika-wf-forms__optsrc-trigger${isUnset ? " kuika-wf-forms__optsrc-trigger--unset" : ""}`}
        onClick={(): void => setOptionsSourceTarget(`${section.id}:${field.id}`)}
        title={
          isUnset
            ? `Not set — this dropdown has no options yet. Click to choose where they come from.`
            : `Options: ${describeOptionsSource(source)}`
        }
        aria-label="Options source"
        data-testid={`processFormsOptionsSource-${field.id}`}
      >
        <KuikaSvg
          name={isUnset ? "alert-triangle" : "list-numbers"}
          width={14}
          height={14}
          color={isUnset ? "var(--orange-6, #f59e0b)" : "var(--dark-9)"}
        />
      </button>
    );
  };

  const renderRow = (section: IFormSection, field: ICRUDFormField, rowIndex: number) => {
    const output: boolean = isOutput(field);
    const badge: IFieldBadge = badgeFor(field);
    const elementOptions: IElementTypeOption[] = getElementTypeOptions(field.typeName, tableTypeNames);
    // An output parameter the user has switched off — flag it so dropping a mapped output is deliberate.
    const outputUnselected: boolean = output && !field.isEnabled;
    // Drag affordances: dim the row being dragged; draw the insertion line at the hovered slot
    // (above this row, or below it when this is the section's last row).
    const isDragging: boolean = dragFieldId === field.id;
    const inSection: boolean = dropTarget?.sectionId === section.id;
    const dropBefore: boolean = inSection && dropTarget?.index === rowIndex;
    const dropAfter: boolean = inSection && rowIndex === section.items.length - 1 && dropTarget?.index === rowIndex + 1;
    const rowClass: string =
      "kuika-wf-forms__row" +
      (outputUnselected ? " kuika-wf-forms__row--warn" : "") +
      (isDragging ? " kuika-wf-forms__row--dragging" : "") +
      (dropBefore ? " kuika-wf-forms__row--drop-before" : "") +
      (dropAfter ? " kuika-wf-forms__row--drop-after" : "");

    return (
      <div className={rowClass} key={field.id} data-testid={`processFormsField-${field.id}`}>
        <span className="kuika-wf-forms__cell kuika-wf-forms__cell--check">
          <span
            className="kuika-wf-forms__grip"
            onMouseDown={(e: React.MouseEvent<HTMLSpanElement>): void => handleGripMouseDown(e, field.id)}
            title="Drag to reorder, or to move to another section"
            data-testid={`processFormsFieldGrip-${field.id}`}
          >
            <KuikaSvg name="grip-vertical" width={12} height={14} color="var(--dark-7)" />
          </span>
          <KuikaCheckbox
            checked={field.isEnabled}
            onChange={(): void => mutateField(section.id, field.id, { isEnabled: !field.isEnabled })}
          />
        </span>

        <span className="kuika-wf-forms__cell kuika-wf-forms__cell--label">
          <KuikaInput
            size="md"
            value={field.name}
            onChange={(_e: React.ChangeEvent<HTMLInputElement>, value: string | number): void =>
              mutateField(section.id, field.id, { name: String(value) })
            }
            data-testid={`processFormsFieldLabel-${field.id}`}
          />
        </span>

        <span className="kuika-wf-forms__cell kuika-wf-forms__cell--param">
          <span className="kuika-wf-forms__param-name">{field.linkedWorkflowBoParameterName || "—"}</span>
          <span className={`kuika-wf-forms__badge kuika-wf-forms__badge--${badge.kind}`}>{badge.text}</span>
          {output && (
            <span
              className="kuika-wf-forms__badge kuika-wf-forms__badge--output"
              title="This step maps this parameter as an output — it's pre-selected."
            >
              Output
            </span>
          )}
        </span>

        <span className="kuika-wf-forms__cell kuika-wf-forms__cell--type">
          {field.source === EFormFieldSource.Custom ? (
            <KuikaSelectbox
              size="md"
              placeholder="Select field type"
              value={field.typeName}
              options={CUSTOM_FIELD_TYPES.map((t: IFieldTypeOption) => ({ value: t.value, label: t.label }))}
              onChange={(value: string | number | (string | number)[] | null): void => {
                const typeName: string = value == null ? "" : String(value);
                mutateField(section.id, field.id, { typeName, elementType: defaultElementTypeFor(typeName) });
              }}
            />
          ) : (
            field.typeName || "—"
          )}
        </span>

        <span className="kuika-wf-forms__cell kuika-wf-forms__cell--element">
          <KuikaSelectbox
            size="md"
            value={field.elementType}
            options={elementOptions.map((option: IElementTypeOption) => ({
              value: option.name,
              label: option.label
            }))}
            onChange={(value: string | number | (string | number)[] | null): void =>
              changeElementType(section, field, value == null ? "" : String(value))
            }
          />
          {renderOptionsSourceTrigger(section, field)}
        </span>

        <span className="kuika-wf-forms__cell kuika-wf-forms__cell--flag">
          <KuikaCheckbox
            checked={field.isRequired}
            onChange={(): void => mutateField(section.id, field.id, { isRequired: !field.isRequired })}
          />
        </span>

        <span className="kuika-wf-forms__cell kuika-wf-forms__cell--flag">
          <KuikaCheckbox
            checked={field.isReadonlyField}
            onChange={(): void => mutateField(section.id, field.id, { isReadonlyField: !field.isReadonlyField })}
          />
        </span>

        <span className="kuika-wf-forms__cell kuika-wf-forms__cell--remove">
          {/* Business Object params are always on the form — unselect them, never delete. Only added
              fields (internal params + custom fields) can be removed. The unselected-output warning
              lives here too, in the trash's column, so the row's leading edge stays clean. */}
          {outputUnselected && (
            <span
              className="kuika-wf-forms__output-warn"
              title="This parameter is a step output — leaving it off means the form won't capture it."
            >
              <KuikaSvg name="alert-triangle" width={13} height={13} color="var(--orange-6, #f59e0b)" />
            </span>
          )}
          {!isBoParam(field) && (
            <KuikaSvg
              name="trash"
              width={14}
              height={14}
              pointer
              color="var(--dark-9)"
              onClick={(): void => removeField(section.id, field.id)}
              data-testid={`processFormsFieldRemove-${field.id}`}
            />
          )}
        </span>
      </div>
    );
  };

  // Empty-section body: the first section gets a rich "no fields yet" prompt; later sections show a
  // dashed drop target. Split out so renderSection keeps a single (table vs empty) ternary.
  const renderSectionEmpty = (section: IFormSection, index: number) => {
    if (index === 0) {
      return (
        <div className="kuika-wf-forms__section-blank">
          <span className="kuika-wf-forms__section-blank-icon">
            <KuikaSvg name="search" width={16} height={16} color="var(--dark-8)" />
          </span>
          <span className="kuika-wf-forms__section-blank-title">No fields yet</span>
          <span className="kuika-wf-forms__section-blank-desc">
            This screen shows fields you add below — from this step&apos;s outputs (configured from the diagram) or any
            other parameter.
          </span>
          <div className="kuika-wf-forms__section-blank-actions">
            <KuikaButton
              type="primary"
              size="md"
              uppercase
              onClick={(): void => onOpenAddField(section.id)}
              data-testid="processFormsBlankAddField"
            >
              <KuikaSvg name="plus" width={14} height={14} />
              Add Field
            </KuikaButton>
            <span className="kuika-wf-forms__section-blank-or">or</span>
            <button
              type="button"
              className="kuika-wf-forms__section-blank-link"
              onClick={(): void => addCustomField(section.id)}
              data-testid="processFormsBlankCustom"
            >
              Create a Custom Field Instead
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="kuika-wf-forms__section-empty">
        <span>This section will appear as a separate block on the form.</span>
        <span className="kuika-wf-forms__section-drag-hint">
          <KuikaSvg name="drag-drop" width={13} height={13} color="var(--dark-8)" />
          Drag fields here, or add them below
        </span>
      </div>
    );
  };

  const renderSection = (section: IFormSection, index: number) => {
    const isDropOver: boolean = dropTarget?.sectionId === section.id;
    return (
      <div
        className={`kuika-wf-forms__section${isDropOver ? " kuika-wf-forms__section--dropover" : ""}`}
        key={section.id}
        data-dnd-section-id={section.id}
        data-testid={`processFormsSection-${section.id}`}
      >
        <input
          className="kuika-wf-forms__section-title"
          value={section.title ?? ""}
          placeholder={`Section ${index + 1}`}
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => setSectionTitle(section.id, e.target.value)}
          data-testid={`processFormsSectionTitle-${section.id}`}
        />
        {section.items.length > 0 ? (
          <div className="kuika-wf-forms__table">
            <div className="kuika-wf-forms__row kuika-wf-forms__row--head">
              <span className="kuika-wf-forms__cell kuika-wf-forms__cell--check" />
              <span className="kuika-wf-forms__cell kuika-wf-forms__cell--label">Label</span>
              <span className="kuika-wf-forms__cell kuika-wf-forms__cell--param">Process Parameters</span>
              <span className="kuika-wf-forms__cell kuika-wf-forms__cell--type">Field Type</span>
              <span className="kuika-wf-forms__cell kuika-wf-forms__cell--element">Element Type</span>
              <span className="kuika-wf-forms__cell kuika-wf-forms__cell--flag">Required</span>
              <span className="kuika-wf-forms__cell kuika-wf-forms__cell--flag">Read-Only</span>
              <span className="kuika-wf-forms__cell kuika-wf-forms__cell--remove" />
            </div>
            {section.items.map((field: ICRUDFormField, rowIndex: number) => renderRow(section, field, rowIndex))}
          </div>
        ) : (
          renderSectionEmpty(section, index)
        )}
        <KuikaDropdownMenu
          placement="bottomLeft"
          options={[
            {
              key: "param",
              label: "Add parameter",
              icon: <KuikaSvg name="components" width={14} height={14} />,
              onClick: (): void => onOpenAddField(section.id)
            },
            {
              key: "custom",
              label: "Add custom field",
              icon: <KuikaSvg name="square-plus" width={14} height={14} />,
              onClick: (): void => addCustomField(section.id)
            }
          ]}
        >
          <KuikaButton type="secondary" size="md" uppercase data-testid={`processFormsAddField-${section.id}`}>
            <KuikaSvg name="plus" width={14} height={14} />
            Add Field
            <KuikaSvg name="chevron-down" type="arrows" width={14} height={14} />
          </KuikaButton>
        </KuikaDropdownMenu>
      </div>
    );
  };

  /**
   * The passive trailing drop target from the design: the NEXT section, rendered as a ghost.
   * Dropping a field on it creates that section for real (with the field inside); it never
   * persists by itself — the real sections are the only ones in the metadata.
   */
  const renderGhostSection = () => {
    if (metadata.sections.length === 0) return null;
    const isDropOver: boolean = dropTarget?.sectionId === GHOST_SECTION_ID;
    return (
      <div
        className={`kuika-wf-forms__section kuika-wf-forms__section--ghost${
          isDropOver ? " kuika-wf-forms__section--dropover" : ""
        }`}
        data-dnd-section-id={GHOST_SECTION_ID}
        data-testid="processFormsGhostSection"
      >
        <span className="kuika-wf-forms__section-title kuika-wf-forms__section-title--ghost">
          Section {metadata.sections.length + 1}
        </span>
        <div className="kuika-wf-forms__section-empty">
          <span>This section will appear as a separate block on the form.</span>
          <span className="kuika-wf-forms__section-drag-hint">
            <KuikaSvg name="drag-drop" width={13} height={13} color="var(--blue-6)" />
            Drag a field here to create it
          </span>
          <span className="kuika-wf-forms__ghost-bars" aria-hidden="true">
            <span />
            <span />
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="kuika-wf-forms__form" ref={formRef} data-testid="processFormsForm">
      {isDraft && (
        <div className="kuika-wf-forms__draft-banner" data-testid="processFormsDraftBanner">
          <KuikaSvg name="alert-triangle" width={16} height={16} color="var(--orange-6, #f59e0b)" />
          <span>
            This screen is a draft — click <strong>Apply Screens</strong> to save your changes, otherwise this
            step&apos;s configuration may not be preserved.
          </span>
        </div>
      )}

      <div className="kuika-wf-forms__form-body">
        <FormsTabStepOutputs params={outputParams} />
        {outputParams.length > 0 && (
          <div className="kuika-wf-forms__outputs-note" data-testid="processFormsOutputsNote">
            This step has {outputParams.length} parameter{outputParams.length === 1 ? "" : "s"} configured as{" "}
            {outputParams.length === 1 ? "an output" : "outputs"} from the diagram — they&apos;re shown for reference.
            Use + Add Field to bring any of them (or a new field) onto this screen.
          </div>
        )}
        <div className="kuika-wf-forms__form-header">
          <span className="kuika-wf-forms__form-title">
            <KuikaSvg name="components" width={16} height={16} color="var(--dark-11)" />
            <KuikaTextMedium>{formTitle}</KuikaTextMedium>
          </span>
          <button
            type="button"
            className="kuika-wf-forms__form-layout"
            onClick={(): void => setLayoutOpen(true)}
            data-testid="processFormsOpenLayout"
          >
            <KuikaSvg name="layout-columns" width={14} height={14} />
            Form Layout
          </button>
        </div>

        {hasCustomField && (
          <div className="kuika-wf-forms__custom-note" data-testid="processFormsCustomNote">
            <KuikaSvg name="info-circle" width={14} height={14} color="var(--tone-blue-fg)" />
            <span>
              Custom fields aren&apos;t bound to a workflow parameter — they show on the form, but you must bind them to
              an action on the screen yourself to persist their values.
            </span>
          </div>
        )}

        {metadata.sections.map((section: IFormSection, index: number) => renderSection(section, index))}

        {renderGhostSection()}

        <KuikaButton
          type="text"
          size="md"
          onClick={addSection}
          disabled={metadata.sections.length === 0}
          data-testid="processFormsAddSection"
        >
          <KuikaSvg name="plus" width={14} height={14} />
          Add Section
        </KuikaButton>
      </div>

      <FormsTabLayoutDrawer
        isOpen={layoutOpen}
        metadata={metadata}
        onClose={(): void => setLayoutOpen(false)}
        onSave={(updated: ICRUDFormDiMetadata): void => {
          onChange(updated);
          setLayoutOpen(false);
        }}
      />

      {optionsSourceField !== undefined && (
        <FormsTabOptionsSourceModal
          isOpen
          fieldName={optionsSourceField.field.name || "this field"}
          fieldTypeName={optionsSourceField.field.typeName}
          value={optionsSourceField.field.optionsSource}
          actions={optionSourceActions}
          onClose={(): void => setOptionsSourceTarget(null)}
          onSave={(source: ICRUDFieldOptionsSource | undefined): void => {
            mutateField(optionsSourceField.section.id, optionsSourceField.field.id, {
              optionsSource: source
            });
            setOptionsSourceTarget(null);
          }}
        />
      )}
    </div>
  );
};

export default FormsTabForm;
